
import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Compass } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface QuizSectionProps {
  isLoggedIn: boolean;
}

// Memoize component to prevent unnecessary re-renders
export const QuizSection = memo(({ isLoggedIn }: QuizSectionProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const handleStartQuiz = () => {
    if (!isLoggedIn) {
      toast({
        title: "Authentication required",
        description: "Please sign in to take the quiz",
      });
      navigate('/auth');
      return;
    }
    navigate('/wizard');
  };

  return (
    <div className={cn(
      "bg-white rounded-lg shadow-sm h-full flex flex-col justify-between",
      isMobile ? "p-4" : "p-5 sm:p-6 md:p-8"
    )}>
      <div className="text-center">
        <Compass className={cn(
          "mx-auto mb-2",
          isMobile ? "w-8 h-8" : "w-12 h-12",
          "text-primary"
        )} aria-hidden="true" />
        
        <h2 className={cn(
          "font-bold mb-2",
          isMobile ? "text-lg" : "text-2xl"
        )}>
          Discover Your Perfect Destination
        </h2>
        
        {!isMobile && (
          <p className="text-gray-600 mb-6">
            Take our quick quiz to get personalized travel recommendations.
          </p>
        )}
        
        <Button 
          size={isMobile ? "sm" : "lg"}
          onClick={handleStartQuiz}
          className="w-full"
          aria-label={isLoggedIn ? 'Start personalized travel quiz' : 'Log in to start travel quiz'}
        >
          {isLoggedIn ? 'Start Quiz Now' : 'Login to Start Quiz'}
        </Button>
      </div>
    </div>
  );
});
